import { useEffect, useState } from "react";
import { AuthResponse } from "../types";

interface ConfirmationViewProps {
  eventData: AuthResponse;
  onBackToEvent: () => void;
}

export function ConfirmationView({
  eventData,
  onBackToEvent,
}: ConfirmationViewProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Trigger confetti animation
    const confettiTimer = setTimeout(() => setShowConfetti(true), 300);
    const contentTimer = setTimeout(() => setShowContent(true), 600);

    return () => {
      clearTimeout(confettiTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  const handleAddToCalendar = () => {
    const event = eventData.event;
    const guestName = eventData.guest.name;

    // Check if event has a date
    if (!event.date) {
      alert("This event doesn't have a date set. Please contact the host for more information.");
      return;
    }

    // Format the event date for calendar URLs
    const eventDateStr = event.date; // Expected format: YYYY-MM-DD
    
    // Validate the date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(eventDateStr)) {
      alert("Invalid event date format. Please contact the host for more information.");
      return;
    }
    
    let startDate: string;
    let endDate: string;
    
    // Check if event has a time specified
    if (event.time && event.time.trim()) {
      // Parse the time string - supports both formats:
      // 24-hour: "18:00" or "18:00 - 21:00"
      // 12-hour: "6:00 PM" or "6:00 PM - 9:00 PM"
      
      // Try 24-hour format first (HH:MM)
      let timeMatch = event.time.match(/(\d{1,2}):(\d{2})(?!\s*[AP]M)/i);
      let is24Hour = true;
      
      // If no 24-hour match, try 12-hour format with AM/PM
      if (!timeMatch) {
        timeMatch = event.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        is24Hour = false;
      }
      
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2];
        
        // Convert to 24-hour format if needed
        if (!is24Hour && timeMatch[3]) {
          const meridiem = timeMatch[3].toUpperCase();
          if (meridiem === 'PM' && hours !== 12) hours += 12;
          if (meridiem === 'AM' && hours === 12) hours = 0;
        }
        
        const hoursStr = hours.toString().padStart(2, '0');
        
        // Create datetime in local timezone format (YYYYMMDDTHHMMSS)
        startDate = `${eventDateStr.replace(/[-]/g, "")}T${hoursStr}${minutes}00`;
        
        // For end time, check if there's a range
        let endTimeMatch;
        if (is24Hour) {
          endTimeMatch = event.time.match(/-\s*(\d{1,2}):(\d{2})(?!\s*[AP]M)/i);
        } else {
          endTimeMatch = event.time.match(/-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        }
        
        if (endTimeMatch) {
          let endHours = parseInt(endTimeMatch[1]);
          const endMinutes = endTimeMatch[2];
          
          // Convert to 24-hour format if needed
          if (!is24Hour && endTimeMatch[3]) {
            const endMeridiem = endTimeMatch[3].toUpperCase();
            if (endMeridiem === 'PM' && endHours !== 12) endHours += 12;
            if (endMeridiem === 'AM' && endHours === 12) endHours = 0;
          }
          
          // Check if event goes into next day (end time < start time)
          let endDateStr = eventDateStr;
          if (endHours < hours || (endHours === hours && parseInt(endMinutes) < parseInt(minutes))) {
            // Add one day to the end date
            const eventDateObj = new Date(eventDateStr + 'T00:00:00');
            eventDateObj.setDate(eventDateObj.getDate() + 1);
            endDateStr = eventDateObj.toISOString().split('T')[0];
          }
          
          const endHoursStr = endHours.toString().padStart(2, '0');
          endDate = `${endDateStr.replace(/[-]/g, "")}T${endHoursStr}${endMinutes}00`;
        } else {
          // No end time specified, add 2 hours to start time
          let endHours = hours + 2;
          let endDateStr = eventDateStr;
          
          // Check if adding 2 hours goes into next day
          if (endHours >= 24) {
            endHours = endHours % 24;
            const eventDateObj = new Date(eventDateStr + 'T00:00:00');
            eventDateObj.setDate(eventDateObj.getDate() + 1);
            endDateStr = eventDateObj.toISOString().split('T')[0];
          }
          
          const endHoursStr = endHours.toString().padStart(2, '0');
          endDate = `${endDateStr.replace(/[-]/g, "")}T${endHoursStr}${minutes}00`;
        }
      } else {
        // Time format not recognized, fall back to all-day event
        startDate = eventDateStr.replace(/[-]/g, "");
        endDate = startDate;
      }
    } else {
      // No time specified - create all-day event
      // Use YYYYMMDD format (no time, no timezone) to prevent timezone conversion
      startDate = eventDateStr.replace(/[-]/g, "");
      endDate = startDate;
    }

    // Create calendar URLs for different platforms
    const title = encodeURIComponent(event.name);
    const description = encodeURIComponent(
      `Hello ${guestName}! ${event.description || ""}`.trim()
    );
    const location = encodeURIComponent(event.location || "");

    // Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`;

    // Apple Calendar URL (for iOS/macOS) - using the correct format
    const appleCalendarUrl = `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:${startDate}%0ADTEND:${endDate}%0ASUMMARY:${title}%0ADESCRIPTION:${description}%0ALOCATION:${location}%0AEND:VEVENT%0AEND:VCALENDAR`;

    // Detect if it's a mobile device
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMac = /Macintosh|Mac OS X/.test(navigator.userAgent);

    if (isMobile) {
      if (isIOS) {
        // For iOS, try Apple Calendar first with data URL, then Google Calendar
        const link = document.createElement("a");
        link.href = appleCalendarUrl;
        link.download = `${event.name.replace(/[^a-zA-Z0-9]/g, "_")}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Fallback to Google Calendar after a short delay
        setTimeout(() => {
          window.open(googleCalendarUrl, "_blank");
        }, 2000);
      } else {
        // Android - use Google Calendar
        window.open(googleCalendarUrl, "_blank");
      }
    } else if (isMac) {
      // For macOS, try Apple Calendar first, then Google Calendar
      const link = document.createElement("a");
      link.href = appleCalendarUrl;
      link.download = `${event.name.replace(/[^a-zA-Z0-9]/g, "_")}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Fallback to Google Calendar after a short delay
      setTimeout(() => {
        window.open(googleCalendarUrl, "_blank");
      }, 2000);
    } else {
      // Desktop - try both options
      const useGoogle = confirm(
        "Choose calendar app:\nOK = Google Calendar\nCancel = Download .ics file"
      );
      if (useGoogle) {
        window.open(googleCalendarUrl, "_blank");
      } else {
        // Fallback to .ics download for desktop
        downloadICSFile();
      }
    }
  };

  const downloadICSFile = async () => {
    try {
      const response = await fetch(
        `/api/rsvp/calendar/${
          eventData.event.id
        }.ics?guestName=${encodeURIComponent(eventData.guest.name)}`
      );

      if (!response.ok) {
        throw new Error("Failed to download calendar file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${eventData.event.name.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading calendar:", error);
      alert("Failed to download calendar file. Please try again.");
    }
  };

  return (
    <div className="confirmation-container">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className={`confetti confetti-${i % 6}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Main confirmation card */}
      <div className={`confirmation-card ${showContent ? "show" : ""}`}>
        {/* Success Icon with Animation */}
        <div className="success-icon-container">
          <div className="success-icon">
            <div className="icon-background">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="checkmark"
              >
                <path
                  d="M9 12l2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="icon-ring"></div>
            <div className="icon-particles">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`particle particle-${i}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="success-content">
          <h1 className="success-title">
            <span className="title-text">You're All Set!</span>
            <span className="title-emoji">🎉</span>
          </h1>
          <p className="success-subtitle">
            Your RSVP has been confirmed and the host has been notified.
          </p>
          <p className="success-message">
            We can't wait to see you at the event! Get ready for an amazing
            time.
          </p>

          {/* Calendar Download Button - Only show if event has a date */}
          {eventData.event.date && (
            <div className="calendar-download-section">
              <button
                className="calendar-download-btn"
                onClick={handleAddToCalendar}
              >
                <span className="calendar-icon">📅</span>
                Add to Calendar
              </button>
              <p className="calendar-download-text">
                Add this event to your calendar app
              </p>
            </div>
          )}

          {/* Back to Flyer Button */}
          <div className="back-to-flyer-section">
            <button className="back-to-flyer-btn" onClick={onBackToEvent}>
              <span className="back-icon">←</span>
              Back to Flyer
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="decorative-elements">
          <div className="sparkle sparkle-1">✨</div>
          <div className="sparkle sparkle-2">⭐</div>
          <div className="sparkle sparkle-3">✨</div>
          <div className="sparkle sparkle-4">🌟</div>
        </div>

        {/* Bottom gradient */}
        <div className="bottom-gradient"></div>
      </div>

      <style>{`
        .confirmation-container {
          min-height: 100vh;
          min-height: 100dvh;
          background: linear-gradient(135deg, #2C5F5D 0%, #1a4a47 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          overflow: hidden;
        }

        .bg-animation {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 1;
        }

        .floating-shape {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 10s ease-in-out infinite;
          backdrop-filter: blur(1px);
        }

        .shape-1 {
          width: 120px;
          height: 120px;
          top: 10%;
          left: 5%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 80px;
          height: 80px;
          top: 70%;
          right: 10%;
          animation-delay: 2s;
        }

        .shape-3 {
          width: 100px;
          height: 100px;
          bottom: 20%;
          left: 10%;
          animation-delay: 4s;
        }

        .shape-4 {
          width: 140px;
          height: 140px;
          top: 5%;
          right: 20%;
          animation-delay: 1s;
        }

        .shape-5 {
          width: 60px;
          height: 60px;
          top: 50%;
          left: 80%;
          animation-delay: 3s;
        }

        .shape-6 {
          width: 90px;
          height: 90px;
          bottom: 10%;
          right: 5%;
          animation-delay: 5s;
        }

        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1); 
            opacity: 0.6;
          }
          50% { 
            transform: translateY(-40px) rotate(180deg) scale(1.2); 
            opacity: 0.8;
          }
        }

        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 2;
        }

        .confetti {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #2C5F5D;
          animation: confetti-fall linear infinite;
          opacity: 0.6;
        }

        .confetti-0 { background: #2C5F5D; }
        .confetti-1 { background: #1a4a47; }
        .confetti-2 { background: #2C5F5D; }
        .confetti-3 { background: #1a4a47; }
        .confetti-4 { background: #2C5F5D; }
        .confetti-5 { background: #1a4a47; }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .confirmation-card {
          background: rgba(255, 255, 255, 0.75); /* Glassmorphic */
          backdrop-filter: blur(30px) saturate(150%);
          -webkit-backdrop-filter: blur(30px) saturate(150%);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          box-shadow: 
            0 20px 40px -12px rgba(0, 0, 0, 0.15);
          max-width: 500px;
          width: calc(100% - 2rem);
          padding: 3rem 2rem;
          position: relative;
          z-index: 3;
          transform: translateY(30px);
          opacity: 0;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          margin: 1rem;
        }

        .confirmation-card.show {
          transform: translateY(0);
          opacity: 1;
        }

        .success-icon-container {
          display: flex;
          justify-content: center;
          margin-bottom: 2rem;
          position: relative;
        }

        .success-icon {
          position: relative;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .icon-background {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #2C5F5D 0%, #1a4a47 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 
            0 20px 40px rgba(44, 95, 93, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
          animation: icon-pulse 2s ease-in-out infinite;
        }

        .checkmark {
          width: 48px;
          height: 48px;
          color: white;
          animation: checkmark-draw 0.8s ease-in-out 0.5s both;
        }

        .icon-ring {
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          border: 3px solid rgba(44, 95, 93, 0.3);
          border-radius: 50%;
          animation: ring-expand 1.5s ease-out 0.3s both;
        }

        .icon-particles {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: #2C5F5D;
          border-radius: 50%;
          animation: particle-burst 1.2s ease-out 0.8s both;
        }

        .particle-0 { top: 20%; left: 20%; animation-delay: 0.8s; }
        .particle-1 { top: 20%; right: 20%; animation-delay: 0.9s; }
        .particle-2 { bottom: 20%; left: 20%; animation-delay: 1.0s; }
        .particle-3 { bottom: 20%; right: 20%; animation-delay: 1.1s; }
        .particle-4 { top: 50%; left: 10%; animation-delay: 1.2s; }
        .particle-5 { top: 50%; right: 10%; animation-delay: 1.3s; }
        .particle-6 { top: 10%; left: 50%; animation-delay: 1.4s; }
        .particle-7 { bottom: 10%; left: 50%; animation-delay: 1.5s; }

        @keyframes icon-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes checkmark-draw {
          0% { 
            stroke-dasharray: 0 24;
            opacity: 0;
          }
          100% { 
            stroke-dasharray: 24 24;
            opacity: 1;
          }
        }

        @keyframes ring-expand {
          0% { 
            transform: scale(0.8);
            opacity: 0;
          }
          50% { 
            opacity: 1;
          }
          100% { 
            transform: scale(1.2);
            opacity: 0;
          }
        }

        @keyframes particle-burst {
          0% { 
            transform: scale(0) translate(0, 0);
            opacity: 1;
          }
          100% { 
            transform: scale(1) translate(var(--dx, 0), var(--dy, 0));
            opacity: 0;
          }
        }

        .particle-0 { --dx: -30px; --dy: -30px; }
        .particle-1 { --dx: 30px; --dy: -30px; }
        .particle-2 { --dx: -30px; --dy: 30px; }
        .particle-3 { --dx: 30px; --dy: 30px; }
        .particle-4 { --dx: -40px; --dy: 0px; }
        .particle-5 { --dx: 40px; --dy: 0px; }
        .particle-6 { --dx: 0px; --dy: -40px; }
        .particle-7 { --dx: 0px; --dy: 40px; }

        .success-content {
          text-align: center;
          margin-bottom: 2rem;
        }

        .success-title {
          font-size: 2.5rem;
          font-weight: 600;
          margin: 0 0 1rem 0;
          letter-spacing: -0.025em;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .title-text {
          background: linear-gradient(135deg, #2C5F5D 0%, #1a4a47 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .title-emoji {
          font-size: 2.5rem;
          filter: none;
          -webkit-text-fill-color: initial;
        }

        .success-subtitle {
          font-size: 1.25rem;
          color: #64748b;
          margin: 0 0 1rem 0;
          font-weight: 500;
          line-height: 1.5;
          letter-spacing: 0.025em;
        }

        .success-message {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
          line-height: 1.6;
          font-weight: 400;
          letter-spacing: 0.025em;
        }

        .calendar-download-section {
          margin-top: 2rem;
          text-align: center;
        }

        .calendar-download-btn {
          background: linear-gradient(135deg, rgba(44, 95, 93, 0.9) 0%, rgba(26, 74, 71, 0.9) 100%);
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 12px;
          padding: 1rem 2rem;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(44, 95, 93, 0.3);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .calendar-download-btn:hover {
          background: linear-gradient(135deg, rgba(44, 95, 93, 0.95) 0%, rgba(26, 74, 71, 0.95) 100%);
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(44, 95, 93, 0.4),
                      0 0 0 1px rgba(255, 255, 255, 0.3) inset;
        }

        .calendar-download-btn:active {
          transform: translateY(0);
        }

        .calendar-icon {
          font-size: 1.2rem;
        }

        .calendar-download-text {
          font-size: 0.9rem;
          color: #64748b;
          margin: 0;
          font-weight: 400;
        }

        .back-to-flyer-section {
          margin-top: 1.5rem;
          text-align: center;
        }

        .back-to-flyer-btn {
          background: rgba(255, 255, 255, 0.1);
          color: #2C5F5D;
          border: 2px solid #2C5F5D;
          border-radius: 12px;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
          backdrop-filter: blur(10px);
        }

        .back-to-flyer-btn:hover {
          background: #2C5F5D;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(44, 95, 93, 0.3);
        }

        .back-to-flyer-btn:active {
          transform: translateY(0);
        }

        .back-icon {
          font-size: 1.1rem;
          font-weight: bold;
        }

        .decorative-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .sparkle {
          position: absolute;
          font-size: 1.2rem;
          animation: sparkle-twinkle 3s ease-in-out infinite;
          opacity: 0.4;
        }

        .sparkle-1 {
          top: 15%;
          left: 10%;
          animation-delay: 0s;
        }

        .sparkle-2 {
          top: 25%;
          right: 15%;
          animation-delay: 0.5s;
        }

        .sparkle-3 {
          bottom: 30%;
          left: 15%;
          animation-delay: 1s;
        }

        .sparkle-4 {
          bottom: 20%;
          right: 10%;
          animation-delay: 1.5s;
        }

        @keyframes sparkle-twinkle {
          0%, 100% { 
            opacity: 0.2;
            transform: scale(0.9) rotate(0deg);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.1) rotate(90deg);
          }
        }

        .bottom-gradient {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background: linear-gradient(to top, rgba(255, 255, 255, 0.1), transparent);
          border-radius: 0 0 32px 32px;
        }

        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .confirmation-card {
            padding: 2.5rem 1.5rem;
            border-radius: 16px;
            width: calc(100% - 1.5rem);
            margin: 0.75rem;
          }

          .success-icon {
            width: 100px;
            height: 100px;
          }

          .checkmark {
            width: 40px;
            height: 40px;
          }

          .success-title {
            font-size: 2rem;
          }

          .success-subtitle {
            font-size: 1.1rem;
          }

          .success-message {
            font-size: 0.95rem;
          }

          .calendar-download-btn {
            padding: 0.875rem 1.5rem;
            font-size: 1rem;
          }

          .calendar-download-text {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .confirmation-card {
            padding: 2rem 1.25rem;
            border-radius: 12px;
            width: calc(100% - 1rem);
            margin: 0.5rem;
          }

          .success-title {
            font-size: 1.75rem;
          }

          .success-subtitle {
            font-size: 1rem;
          }

          .calendar-download-btn {
            padding: 0.75rem 1.25rem;
            font-size: 0.95rem;
          }

          .calendar-download-text {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  );
}
